import { Browser } from '@capacitor/browser'
import { consola } from 'consola'
import { isNativePlatform, shouldUseCapacitorBrowser } from '@shared/lib'
import { getClientFingerprint } from '@shared/lib/auth'
import type {
  TelegramAuthInitiateParams,
  TelegramAuthInitiateResponse,
  TelegramAuthStatusResponse,
} from '@entities/auth'
import type { Institution } from '@entities/institution'
import apiAuth from '~/entities/auth'

const logger = consola.withTag('TelegramAuthService')

const MAX_POLL_DURATION = 5 * 60 * 1000 // 5 минут
const POLL_INTERVAL_MS = 5000 // 5 секунд

/**
 * Сервис для работы с авторизацией через Telegram
 * Следует принципам SOLID (Single Responsibility, Open/Closed)
 * 
 * Поддерживает:
 * - Веб-платформу (window.open / window.location.href)
 * - Мобильные платформы через Capacitor Browser
 * - Polling механизм для проверки статуса
 */
export const TelegramAuthService = {
  /**
   * Проверяет, включена ли Telegram авторизация для данного заведения.
   */
  isTelegramAuthEnabled(institution: Institution | null): boolean {
    return institution?.telegram_auth_enabled === true
  },

  /**
   * Возвращает отпечаток устройства.
   * Единая реализация для всех входов живёт в `@shared/lib/auth`.
   */
  async generateClientFingerprint(): Promise<string | undefined> {
    return await getClientFingerprint()
  },

  /**
   * Инициирует процесс авторизации через Telegram.
   * Отправляет запрос на бэкенд для получения ссылки на Telegram бота.
   */
  async initiateAuth(params: TelegramAuthInitiateParams): Promise<TelegramAuthInitiateResponse> {
    logger.info('Инициирование Telegram авторизации с параметрами:', params)
    const response = await apiAuth.generateTelegramToken(params)
    return response.data
  },

  /**
   * Открывает Telegram авторизацию.
   * 
   * Правильный OAuth 2.0 подход (как для Yandex ID):
   * - На мобильных: Capacitor Browser (системный in-app browser)
   * - На вебе: window.open (новая вкладка)
   * 
   * @param url - Ссылка на Telegram бота (https://t.me/...)
   * @returns Объект с информацией об успешности открытия и методе
   */
  async openTelegramUrl(url: string): Promise<{ success: boolean; method: 'capacitor' | 'window' | 'none' }> {
    logger.info('Открытие Telegram авторизации:', url)

    if (shouldUseCapacitorBrowser()) {
      // Мобильная платформа - используем Capacitor Browser (стандартный OAuth подход)
      try {
        await Browser.open({
          url,
          presentationStyle: 'popover', // iOS style
          toolbarColor: '#ffffff',
        })
        
        logger.info('✅ Telegram открыт через Capacitor Browser')
        return { success: true, method: 'capacitor' }
      } catch (error) {
        logger.error('Ошибка открытия Capacitor Browser:', error)
        // Fallback на window.open
        const popup = window.open(url, '_blank')
        return { 
          success: !!popup && !popup.closed, 
          method: popup ? 'window' : 'none', 
        }
      }
    } else {
      // Веб-платформа - используем window.open (новая вкладка)
      try {
        const popup = window.open(url, '_blank')
        
        if (popup && !popup.closed) {
          logger.info('✅ Telegram открыт в новой вкладке')
          return { success: true, method: 'window' }
        } else {
          logger.warn('⚠️ Popup был заблокирован браузером')
          return { success: false, method: 'none' }
        }
      } catch (error) {
        logger.error('Ошибка открытия window.open:', error)
        return { success: false, method: 'none' }
      }
    }
  },

  /**
   * Закрывает Capacitor Browser (для мобильных платформ после успешной авторизации)
   */
  async closeTelegramBrowser(): Promise<void> {
    if (isNativePlatform()) {
      try {
        await Browser.close()
        logger.info('Capacitor Browser закрыт')
      } catch (error) {
        logger.warn('Ошибка закрытия Browser:', error)
      }
    }
  },

  /**
   * Создает контроллер для polling статуса авторизации.
   * @param sessionId ID сессии для опроса.
   * @param onStatusChange Callback для обработки изменения статуса.
   * @param onError Callback для обработки ошибок.
   * @returns Объект с методами start() и stop().
   */
  createPolling(
    sessionId: string,
    onStatusChange: (status: TelegramAuthStatusResponse) => void,
    onError: (error: any) => void,
  ) {
    let pollInterval: NodeJS.Timeout | null = null
    let pollStartTime: number | null = null
    let isActive = false

    const checkStatus = async () => {
      if (!isActive) return

      /* v8 ignore start */
      if (pollStartTime && Date.now() - pollStartTime > MAX_POLL_DURATION) {
        logger.error('Превышено максимальное время ожидания авторизации (5 минут)')
        onError(new Error('Время ожидания авторизации истекло. Попробуйте ещё раз.'))
        stop()
        return
      }
      /* v8 ignore stop */

      try {
        const response = await apiAuth.checkTelegramStatus(sessionId)
        onStatusChange(response.data)

        if (response.data.status === 'completed' || response.data.status === 'expired' || response.data.status === 'failed') {
          stop()
        }
      } catch (error_: any) {
        logger.error('Ошибка при опросе статуса Telegram:', error_)
        
        // Если статус 410 (Gone), токен истёк
        if (error_?.status === 410) {
          onError(new Error('Время авторизации истекло. Попробуйте ещё раз.'))
          stop()
        } else {
          onError(error_)
          stop()
        }
      }
    }

    const start = () => {
      if (isActive) {
        logger.warn('Polling уже активен.')
        return
      }
      isActive = true
      pollStartTime = Date.now()
      logger.info(`Начат опрос статуса для сессии ${sessionId}`)
      checkStatus() // Первая проверка сразу
      pollInterval = setInterval(checkStatus, POLL_INTERVAL_MS)
    }

    const stop = () => {
      if (pollInterval) {
        clearInterval(pollInterval)
        pollInterval = null
        pollStartTime = null
        isActive = false
        logger.info(`Опрос статуса для сессии ${sessionId} остановлен.`)
      }
    }

    return {
      start,
      stop,
      isActive: () => isActive,
    }
  },
};

